import { useDeserializeOnce } from "@/hooks";
import { useLocaleStore } from "@/stores";
import { SerializedNodes, useEditor } from "@craftjs/core";
import clsx from "clsx";
import { ReactNode, useEffect } from "react";
import usePageEditorStore from "../../store/usePageEditorStore";
import Sidebar from "./components/Sidebar";
import Toolbar from "./components/Toolbar";
import Toolbox from "./components/Toolbox";

interface ViewportProps {
    data?: string | SerializedNodes;
    slug?: string;
    enabled: boolean;
    children: ReactNode;
}

const Viewport = ({ data, enabled, children, slug }: ViewportProps) => {
    const activeLanguage = useLocaleStore((state) => state.activeLanguage);

    const {
        connectors,
        actions: { setOptions },
    } = useEditor((state) => ({ enabled: state.options.enabled }));

    useDeserializeOnce({
        contentJsonString: (typeof data === "object" ? JSON.stringify(data) : (data as string)) ?? "{}",
        pageKey: `${slug}:${activeLanguage}`, // any stable version tag
    });

    useEffect(() => {
        if (!window) {
            return;
        }

        window.requestAnimationFrame(() => {
            window.parent.postMessage({ EDITOR_LOADED: true }, "*");

            setTimeout(() => {
                setOptions((options) => {
                    options.enabled = enabled;
                });
            }, 200);
        });
    }, [enabled, setOptions]);

    const fullscreen = usePageEditorStore((state) => state.fullscreen);

    if (!enabled) return <div id="page-editor-content-container">{children}</div>;

    return (
        <div id="viewport" className="w-full">
            <div id="editor-container" className="flex flex-row w-full h-full overflow-hidden">
                <Toolbox />

                <div id="page-container" className="flex grow shrink flex-col h-full">
                    <Toolbar />
                    <div
                        className={clsx([
                            "craftjs-renderer relative w-full h-full flex-1 overflow-x-hidden overflow-y-auto px-4 pb-4 bg-neutral-200",
                            fullscreen
                                ? ""
                                : "min-h-[calc(100vh-70px-50px-50px-150px-36px)] max-h-[calc(100vh-70px-50px-50px-150px-36px)]",
                        ])}
                        id="content-container"
                        ref={(ref: HTMLDivElement) => connectors.select(connectors.hover(ref, ""), "")}
                    >
                        <div id="content" className="relative flex flex-col items-center h-full overflow-x-hidden">
                            <div className="flex-1 border bg-white border-gray-200 w-full">{children}</div>
                        </div>
                    </div>
                </div>

                <Sidebar />
            </div>
        </div>
    );
};

export default Viewport;
